/**
 * Getting at the camera, and explaining it when that fails.
 *
 * Two things bite real broadcasters and neither is obvious from
 * "NotAllowedError":
 *
 *  1. A blocked microphone fails the whole request. Asking for camera and mic
 *     together throws the same error whether it was the camera or the mic that
 *     was refused, so the camera is asked for on its own before giving up —
 *     a service with picture and no sound beats no broadcast at all.
 *  2. On a phone the permission usually has to be given in two places: to the
 *     browser by the operating system, and to this site by the browser. The
 *     guidance below names both, per platform, instead of saying "allow access".
 */

const ua = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent || '');

export const platform = () => {
  const s = ua();
  const iOS = /iPad|iPhone|iPod/.test(s)
    || (/Macintosh/.test(s) && typeof navigator !== 'undefined'
        && navigator.maxTouchPoints > 1);          // iPadOS reports as a Mac
  if (iOS) return /CriOS|FxiOS|EdgiOS/.test(s) ? 'ios-other' : 'ios-safari';
  if (/Android/.test(s)) return 'android';
  return 'desktop';
};

const BROWSER_NAME = () => {
  const s = ua();
  if (/CriOS/.test(s)) return 'Chrome';
  if (/FxiOS/.test(s)) return 'Firefox';
  if (/EdgiOS/.test(s)) return 'Edge';
  return 'your browser';
};

/** Granted, denied, or not asked yet — where the browser will say. */
export async function cameraPermissionState() {
  try {
    const res = await navigator.permissions?.query({ name: 'camera' });
    return res?.state || 'unknown';
  } catch {
    return 'unknown';           // Safari and iOS do not implement this
  }
}

/**
 * The camera, with sound if it is allowed.
 * @returns {Promise<{stream: MediaStream, hasAudio: boolean}>}
 */
export async function requestCameraStream(which = 'environment') {
  if (!navigator.mediaDevices?.getUserMedia) {
    const err = new Error('no-media-devices');
    err.name = 'NotSupportedError';
    throw err;
  }
  // `ideal` rather than a bare value: a bare facingMode is a hard constraint
  // and throws on anything without a rear camera, including every desktop.
  const video = {
    facingMode: { ideal: which },
    width: { ideal: 1280 }, height: { ideal: 720 },
  };
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video, audio: { echoCancellation: true, noiseSuppression: true },
    });
    return { stream, hasAudio: stream.getAudioTracks().length > 0 };
  } catch (e) {
    if (['NotAllowedError', 'NotFoundError', 'NotReadableError',
         'OverconstrainedError', 'TypeError'].includes(e.name)) {
      const stream = await navigator.mediaDevices.getUserMedia({ video });
      return { stream, hasAudio: false };
    }
    throw e;
  }
}

/** What went wrong, and what this person can actually do about it. */
export function describeCameraError(err) {
  const name = err?.name || '';
  const where = platform();
  const browser = BROWSER_NAME();

  if (name === 'NotSupportedError' || !window.isSecureContext) {
    return {
      message: 'This browser will not give a page the camera here.',
      steps: window.isSecureContext
        ? ['Open the page in Safari or Chrome rather than inside another app.']
        : ['Open the site over https — a camera is only offered on a secure connection.'],
    };
  }

  if (name === 'NotAllowedError' || name === 'SecurityError') {
    const steps = {
      'ios-other': [
        `Open the iPad or iPhone Settings app, find ${browser}, and turn Camera and Microphone on.`,
        `Come back to ${browser}, reload this page, and tap Allow when it asks.`,
        'If it never asks, open the page in Safari instead.',
      ],
      'ios-safari': [
        'Tap "aA" at the left of the address bar, then Website Settings.',
        'Set Camera (and Microphone) to Allow, then reload this page.',
        'Check the Settings app → Safari → Camera is not set to Deny.',
      ],
      android: [
        'Tap the lock icon next to the address, then Permissions.',
        'Allow Camera and Microphone, then reload this page.',
      ],
      desktop: [
        'Click the camera or lock icon at the right of the address bar.',
        'Allow the camera for this site, then reload the page.',
      ],
    }[where];
    return {
      message: `${browser === 'your browser' ? 'Your browser' : browser} refused the camera for this page.`,
      steps,
    };
  }

  return {
    message: {
      NotFoundError: 'This device has no camera the browser can use.',
      NotReadableError: 'Another app is already using the camera. Close it and try again.',
      OverconstrainedError: 'This camera cannot provide the requested picture.',
      AbortError: 'The camera stopped responding. Try again.',
    }[name] || `The camera would not open (${name || 'unknown error'}).`,
    steps: [],
  };
}
